import { useState } from 'react';
import { Loader2, Search, ChevronLeft, ChevronRight } from 'lucide-react';
import { YourCheckaNavigation } from '@/components/Navbar';
import { Badge } from '@/components/ui/badge';
import { useAppSelector } from '@/hooks/useAppStore';
import { selectCurrentUser } from '@/store/slices/authSlice';
import { useGetAdminTransactionsQuery } from '@/store/api/apiSlice';
import type { AdminTransaction, AdminTransactionFilters } from '@/types';

function getInitials(firstName?: string, lastName?: string) {
    const first = firstName?.charAt(0) || '';
    const last = lastName?.charAt(0) || '';
    return (first + last).toUpperCase() || 'A';
}

function formatCurrency(amount?: number) {
    return new Intl.NumberFormat('en-NG', { style: 'currency', currency: 'NGN', minimumFractionDigits: 0 }).format(
        amount || 0,
    );
}

const STATUS_BADGE: Record<string, string> = {
    successful: 'bg-green-50 text-green-700 border-green-100',
    pending: 'bg-amber-50 text-amber-700 border-amber-100',
    failed: 'bg-red-50 text-red-700 border-red-100',
    refunded: 'bg-blue-50 text-blue-700 border-blue-100',
    cancelled: 'bg-gray-100 text-gray-600 border-gray-200',
};

const STATUS_OPTIONS = ['', 'successful', 'pending', 'failed', 'refunded', 'cancelled'];
const CHECK_TYPE_OPTIONS = ['', 'standard', 'enhanced', 'pro', 'test'];

export default function AdminTransactions() {
    const currentUser = useAppSelector(selectCurrentUser);

    const [page, setPage] = useState(1);
    const [status, setStatus] = useState('');
    const [checkType, setCheckType] = useState('');
    const [search, setSearch] = useState('');
    const [searchInput, setSearchInput] = useState('');

    const filters: AdminTransactionFilters = {
        page,
        limit: 25,
        ...(status ? { status } : {}),
        ...(checkType ? { checkType } : {}),
        ...(search ? { search } : {}),
    };
    const { data, isLoading, isFetching } = useGetAdminTransactionsQuery(filters);

    const rows = data?.data ?? [];
    const pagination = data?.pagination;
    const summary = data?.summary;

    const applySearch = () => {
        setSearch(searchInput.trim());
        setPage(1);
    };

    const onStatusChange = (v: string) => {
        setStatus(v);
        setPage(1);
    };
    const onCheckTypeChange = (v: string) => {
        setCheckType(v);
        setPage(1);
    };

    return (
        <main className="w-full min-h-screen flex flex-col px-4 py-6 md:px-6 lg:px-8 xl:px-12 bg-[#EDEFF3]">
            <div className="bg-white rounded-3xl shadow-sm flex-1 flex flex-col overflow-hidden">
                <YourCheckaNavigation
                    showTitle={false}
                    showInfo={false}
                    showLocation={false}
                    notificationCount={0}
                    userName={currentUser ? `${currentUser.firstName} ${currentUser.lastName}` : 'Admin'}
                    userInitials={currentUser ? getInitials(currentUser.firstName, currentUser.lastName) : 'A'}
                    only={true}
                    className="border-b border-gray-100"
                />

                <div className="flex-1 overflow-y-auto p-4 md:p-8 lg:p-10 flex flex-col gap-6 w-full">
                    <div>
                        <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">Transactions</h1>
                        <p className="text-sm text-gray-500 mt-1">
                            Every payment across the platform. Totals reflect the whole filtered set.
                        </p>
                    </div>

                    {/* Summary cards */}
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 md:gap-4">
                        <div className="bg-gray-50 rounded-2xl p-4 md:p-5">
                            <p className="text-xs text-gray-500 mb-1">Transactions</p>
                            <p className="text-2xl md:text-3xl font-bold text-gray-900">
                                {summary?.totalTransactions ?? 0}
                            </p>
                        </div>
                        <div className="bg-green-50 rounded-2xl p-4 md:p-5">
                            <p className="text-xs text-green-600 mb-1">Successful revenue</p>
                            <p className="text-2xl md:text-3xl font-bold text-green-900">
                                {formatCurrency(summary?.successfulRevenue)}
                            </p>
                            <p className="text-[11px] text-green-700 mt-1">
                                {summary?.byStatus?.successful?.count ?? 0} successful
                            </p>
                        </div>
                        <div className="bg-amber-50 rounded-2xl p-4 md:p-5">
                            <p className="text-xs text-amber-600 mb-1">Pending</p>
                            <p className="text-2xl md:text-3xl font-bold text-amber-900">
                                {summary?.byStatus?.pending?.count ?? 0}
                            </p>
                        </div>
                        <div className="bg-gray-50 rounded-2xl p-4 md:p-5">
                            <p className="text-xs text-gray-500 mb-1">Failed</p>
                            <p className="text-2xl md:text-3xl font-bold text-gray-700">
                                {summary?.byStatus?.failed?.count ?? 0}
                            </p>
                        </div>
                    </div>

                    {/* Filters */}
                    <div className="flex flex-col md:flex-row md:items-center gap-3 flex-wrap">
                        <div className="relative w-full sm:max-w-xs">
                            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                            <input
                                type="text"
                                placeholder="Search txn id / employee / ref"
                                value={searchInput}
                                onChange={(e) => setSearchInput(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && applySearch()}
                                className="w-full pl-10 pr-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-gray-200 focus:border-transparent"
                            />
                        </div>
                        <select
                            value={status}
                            onChange={(e) => onStatusChange(e.target.value)}
                            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white capitalize focus:outline-none focus:border-[#FF6B35]"
                        >
                            {STATUS_OPTIONS.map((s) => (
                                <option key={s} value={s}>
                                    {s ? s : 'All statuses'}
                                </option>
                            ))}
                        </select>
                        <select
                            value={checkType}
                            onChange={(e) => onCheckTypeChange(e.target.value)}
                            className="px-3 py-2.5 border border-gray-200 rounded-lg text-sm bg-white capitalize focus:outline-none focus:border-[#FF6B35]"
                        >
                            {CHECK_TYPE_OPTIONS.map((c) => (
                                <option key={c} value={c}>
                                    {c ? c : 'All tiers'}
                                </option>
                            ))}
                        </select>
                    </div>

                    {/* Table */}
                    <div className="rounded-2xl border border-gray-100 overflow-hidden">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-16">
                                <Loader2 className="h-6 w-6 text-gray-400 animate-spin" />
                            </div>
                        ) : rows.length === 0 ? (
                            <div className="flex flex-col items-center justify-center text-center py-12 px-6">
                                <p className="text-gray-500 text-sm">No transactions found.</p>
                            </div>
                        ) : (
                            <div className="overflow-x-auto">
                                <table className={`w-full min-w-[920px] ${isFetching ? 'opacity-60' : ''}`}>
                                    <thead className="bg-gray-50/60">
                                        <tr className="text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                            <th className="px-4 py-3">Transaction</th>
                                            <th className="px-4 py-3">Amount</th>
                                            <th className="px-4 py-3">Status</th>
                                            <th className="px-4 py-3">Method</th>
                                            <th className="px-4 py-3">Tier</th>
                                            <th className="px-4 py-3">Promo</th>
                                            <th className="px-4 py-3">Date</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {rows.map((txn) => (
                                            <TxnRow key={txn.id} txn={txn} />
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </div>

                    {/* Pagination */}
                    {pagination && pagination.totalPages > 1 && (
                        <div className="flex items-center justify-between">
                            <p className="text-xs text-gray-500">
                                Page {pagination.page} of {pagination.totalPages} · {pagination.total} total
                            </p>
                            <div className="flex items-center gap-2">
                                <button
                                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                                    disabled={page <= 1 || isFetching}
                                    className="flex items-center gap-1 border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:opacity-40 hover:bg-gray-50"
                                >
                                    <ChevronLeft className="w-4 h-4" /> Prev
                                </button>
                                <button
                                    onClick={() => setPage((p) => Math.min(pagination.totalPages, p + 1))}
                                    disabled={page >= pagination.totalPages || isFetching}
                                    className="flex items-center gap-1 border border-gray-200 rounded-lg px-3 py-2 text-sm disabled:opacity-40 hover:bg-gray-50"
                                >
                                    Next <ChevronRight className="w-4 h-4" />
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </main>
    );
}

function TxnRow({ txn }: { txn: AdminTransaction }) {
    const dateLabel = txn.datePaid || txn.createdAt;
    return (
        <tr className="border-b border-gray-100 hover:bg-gray-50/60 transition-colors">
            <td className="px-4 py-4">
                <p className="text-sm font-medium text-gray-900">{txn.transactionId}</p>
                {txn.employeeName && <p className="text-xs text-gray-500">{txn.employeeName}</p>}
            </td>
            <td className="px-4 py-4 text-sm font-semibold text-gray-900">{formatCurrency(txn.amount)}</td>
            <td className="px-4 py-4">
                <Badge
                    variant="outline"
                    className={`${STATUS_BADGE[txn.status] ?? 'bg-gray-50 text-gray-600 border-gray-200'} px-3 py-1 rounded-lg font-medium capitalize`}
                >
                    {txn.status}
                </Badge>
            </td>
            <td className="px-4 py-4 text-sm text-gray-700 capitalize">{txn.paymentMethod || txn.gateway || '—'}</td>
            <td className="px-4 py-4 text-sm text-gray-700 capitalize">{txn.checkType || '—'}</td>
            <td className="px-4 py-4">
                {txn.promoCode ? (
                    <Badge
                        variant="outline"
                        className="bg-indigo-50 text-indigo-600 border-indigo-100 px-2.5 py-1 rounded-lg font-medium"
                    >
                        {txn.promoCode}
                    </Badge>
                ) : (
                    <span className="text-gray-300 text-sm">—</span>
                )}
            </td>
            <td className="px-4 py-4 text-xs text-gray-500">
                {dateLabel
                    ? new Date(dateLabel).toLocaleDateString('en-GB', {
                          day: '2-digit',
                          month: 'short',
                          year: 'numeric',
                      })
                    : '—'}
            </td>
        </tr>
    );
}
